import LogViewer from './LogViewer';
import { prepareLogRows, canonicalLogAction, redactLogDetails } from './auditLogPresentation';
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";

// ============================================================================
// TRUSTEDGE PAYROLL — ENTERPRISE DATABASE SECURITY & THREAT DEFENSE HUB
// ============================================================================

export const MAX_FAILED_ATTEMPTS = 3;
export const LOCKOUT_MINUTES = 15;
export const LOCKOUT_MS = LOCKOUT_MINUTES * 60 * 1000;
export const ATTEMPT_WINDOW_MS = LOCKOUT_MINUTES * 60 * 1000;

// Local fallback memory cache (for microsecond client-side checks and offline fallback)
function getLocalSec(key) {
  try {
    const raw = localStorage.getItem("tw_sec_" + key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function setLocalSec(key, data) {
  try {
    localStorage.setItem("tw_sec_" + key, JSON.stringify(data));
  } catch (e) {}
}

function clearLocalSec(key) {
  try {
    localStorage.removeItem("tw_sec_" + key);
  } catch (e) {}
}

/**
 * Synchronous local check for instant UI badge rendering
 */
export function getLocalEmployeeLockStatus(employeeId, pan) {
  const panClean = (pan || "").trim().toUpperCase();
  const now = Date.now();
  const local = getLocalSec(`emp_${employeeId || panClean}`);
  if (local && local.lockedUntil && now < local.lockedUntil) {
    const remainingMinutes = Math.ceil((local.lockedUntil - now) / 60000);
    return {
      isLocked: true,
      remainingMinutes: Math.max(1, remainingMinutes),
      attempts: local.attempts || MAX_FAILED_ATTEMPTS,
    };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. FAST O(1) LOCKOUT CHECKS (DATABASE-INDEXED WITH GRACEFUL FALLBACK)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if an employee is locked out.
 * Priority 1: High-performance O(1) query on auth_security_status
 * Priority 2: Audit log fallback (if DB migration is pending)
 */
export async function checkEmployeeLockout(supabase, employeeId, pan) {
  const panClean = (pan || "").trim().toUpperCase();
  if (!panClean && !employeeId) {
    return { isLocked: false, remainingMinutes: 0, attempts: 0 };
  }

  const now = Date.now();

  // 1. Try high-performance O(1) database query
  try {
    const { data, error } = await supabase
      .from("auth_security_status")
      .select("is_locked, locked_until, failed_attempts")
      .eq("account_type", "employee")
      .eq("identifier", panClean)
      .maybeSingle();

    if (!error && data) {
      if (data.is_locked && data.locked_until) {
        const lockUntilMs = new Date(data.locked_until).getTime();
        if (now < lockUntilMs) {
          const remainingMinutes = Math.ceil((lockUntilMs - now) / 60000);
          setLocalSec(`emp_${employeeId || panClean}`, {
            lockedUntil: lockUntilMs,
            attempts: data.failed_attempts || MAX_FAILED_ATTEMPTS,
          });
          return {
            isLocked: true,
            remainingMinutes: Math.max(1, remainingMinutes),
            attempts: data.failed_attempts || MAX_FAILED_ATTEMPTS,
            lockedUntil: data.locked_until,
          };
        } else {
          // Lock duration expired; clear local
          clearLocalSec(`emp_${employeeId || panClean}`);
          return { isLocked: false, remainingMinutes: 0, attempts: 0 };
        }
      }
      return { isLocked: false, remainingMinutes: 0, attempts: data.failed_attempts || 0 };
    }
  } catch (dbErr) {
    // If table doesn't exist, proceed to audit_logs fallback
  }

  // 2. Local memory fallback
  const local = getLocalSec(`emp_${employeeId || panClean}`);
  if (local && local.lockedUntil && now < local.lockedUntil) {
    const remainingMinutes = Math.ceil((local.lockedUntil - now) / 60000);
    return {
      isLocked: true,
      remainingMinutes: Math.max(1, remainingMinutes),
      attempts: local.attempts || MAX_FAILED_ATTEMPTS,
      lockedUntil: new Date(local.lockedUntil).toISOString(),
    };
  }

  // 3. Fallback: Query audit_logs
  try {
    let query = supabase
      .from("audit_logs")
      .select("action, created_at, details")
      .order("created_at", { ascending: false })
      .limit(30);

    if (employeeId) {
      query = query.or(`employee_id.eq.${employeeId},details->>pan.eq.${panClean}`);
    } else {
      query = query.eq("details->>pan", panClean);
    }

    const { data: logs, error } = await query;
    if (error || !logs || logs.length === 0) {
      return { isLocked: false, remainingMinutes: 0, attempts: 0 };
    }
    return calculateLockFromLogs(logs, "EMPLOYEE");
  } catch (err) {
    return { isLocked: false, remainingMinutes: 0, attempts: 0 };
  }
}

/**
 * Check if a company admin or master admin username is locked out.
 */
export async function checkAdminLockout(supabase, username) {
  const uClean = (username || "").trim().toLowerCase();
  if (!uClean) {
    return { isLocked: false, remainingMinutes: 0, attempts: 0 };
  }

  const now = Date.now();

  // 1. Try high-performance O(1) database query
  try {
    const { data, error } = await supabase
      .from("auth_security_status")
      .select("is_locked, locked_until, failed_attempts")
      .eq("account_type", "admin")
      .eq("identifier", uClean)
      .maybeSingle();

    if (!error && data) {
      if (data.is_locked && data.locked_until) {
        const lockUntilMs = new Date(data.locked_until).getTime();
        if (now < lockUntilMs) {
          const remainingMinutes = Math.ceil((lockUntilMs - now) / 60000);
          setLocalSec(`admin_${uClean}`, {
            lockedUntil: lockUntilMs,
            attempts: data.failed_attempts || MAX_FAILED_ATTEMPTS,
          });
          return {
            isLocked: true,
            remainingMinutes: Math.max(1, remainingMinutes),
            attempts: data.failed_attempts || MAX_FAILED_ATTEMPTS,
            lockedUntil: data.locked_until,
          };
        } else {
          clearLocalSec(`admin_${uClean}`);
          return { isLocked: false, remainingMinutes: 0, attempts: 0 };
        }
      }
      return { isLocked: false, remainingMinutes: 0, attempts: data.failed_attempts || 0 };
    }
  } catch (dbErr) {
    // Proceed to fallback
  }

  // 2. Local memory check
  const local = getLocalSec(`admin_${uClean}`);
  if (local && local.lockedUntil && now < local.lockedUntil) {
    const remainingMinutes = Math.ceil((local.lockedUntil - now) / 60000);
    return {
      isLocked: true,
      remainingMinutes: Math.max(1, remainingMinutes),
      attempts: local.attempts || MAX_FAILED_ATTEMPTS,
      lockedUntil: new Date(local.lockedUntil).toISOString(),
    };
  }

  // 3. Fallback: Query audit_logs
  try {
    const { data: logs, error } = await supabase
      .from("audit_logs")
      .select("action, created_at, details")
      .eq("actor_type", "admin")
      .eq("details->>username", uClean)
      .order("created_at", { ascending: false })
      .limit(30);

    if (error || !logs || logs.length === 0) {
      return { isLocked: false, remainingMinutes: 0, attempts: 0 };
    }
    return calculateLockFromLogs(logs, "ADMIN");
  } catch (err) {
    return { isLocked: false, remainingMinutes: 0, attempts: 0 };
  }
}

/**
 * Fallback parser for raw audit logs stream
 */
function calculateLockFromLogs(logs, rolePrefix) {
  const unlockAction = `${rolePrefix}_UNLOCK`;
  const successAction = `${rolePrefix}_LOGIN_SUCCESS`;
  const failAction = `${rolePrefix}_LOGIN_FAILED`;
  const now = Date.now();

  let failedAttempts = 0;
  let thirdFailTime = null;

  for (const log of logs) {
    if (log.action === unlockAction || log.action === successAction) {
      break;
    }
    if (log.action === failAction) {
      const logTime = new Date(log.created_at).getTime();
      if (now - logTime <= ATTEMPT_WINDOW_MS + LOCKOUT_MS) {
        failedAttempts++;
        if (failedAttempts === MAX_FAILED_ATTEMPTS) {
          thirdFailTime = logTime;
        }
      } else {
        break;
      }
    }
  }

  if (failedAttempts >= MAX_FAILED_ATTEMPTS && thirdFailTime) {
    const lockedUntil = thirdFailTime + LOCKOUT_MS;
    if (now < lockedUntil) {
      const remainingMinutes = Math.ceil((lockedUntil - now) / 60000);
      return {
        isLocked: true,
        remainingMinutes: Math.max(1, remainingMinutes),
        attempts: failedAttempts,
        lockedUntil: new Date(lockedUntil).toISOString(),
      };
    }
  }

  return { isLocked: false, remainingMinutes: 0, attempts: failedAttempts };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. RECORD ATTEMPTS (ATOMIC DB RPC -> DIRECT TABLE -> AUDIT LOG FALLBACK)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Record a failed employee login attempt and enforce 3-strike lockout
 */
export async function recordFailedEmployeeLogin(supabase, companyId, employeeId, pan, reason) {
  const panClean = (pan || "").trim().toUpperCase();
  const now = Date.now();
  const targetKey = `emp_${employeeId || panClean}`;

  // Priority 1: Call atomic stored procedure handle_login_failure
  try {
    const { data: rpcRes, error: rpcErr } = await supabase.rpc("handle_login_failure", {
      p_account_type: "employee",
      p_identifier: panClean,
      p_company_id: companyId || null,
      p_employee_id: employeeId || null,
      p_reason: reason || "Invalid credentials",
    });

    if (!rpcErr && rpcRes) {
      if (rpcRes.isLocked) {
        setLocalSec(targetKey, {
          lockedUntil: now + (rpcRes.remainingMinutes || LOCKOUT_MINUTES) * 60000,
          attempts: rpcRes.attempts || MAX_FAILED_ATTEMPTS,
        });
      }
      // Also write to audit_logs for backward-compatible compliance history
      logLegacyAudit(supabase, companyId, "employee", employeeId, rpcRes.isLocked ? "EMPLOYEE_LOCKED" : "EMPLOYEE_LOGIN_FAILED", {
        pan: panClean,
        reason: reason || "Invalid credentials",
        failed_attempts: rpcRes.attempts,
        locked: rpcRes.isLocked,
      });

      return {
        isLocked: !!rpcRes.isLocked,
        remainingMinutes: rpcRes.remainingMinutes || 0,
        attempts: rpcRes.attempts || 1,
      };
    }
  } catch (e) {
    // Stored procedure not available, try direct table upsert
  }

  // Priority 2: Direct upsert on auth_security_status
  try {
    const { data: existing } = await supabase
      .from("auth_security_status")
      .select("*")
      .eq("account_type", "employee")
      .eq("identifier", panClean)
      .maybeSingle();

    let attempts = 1;
    let isLocked = false;
    let remainingMinutes = 0;
    let lockedUntil = null;

    if (existing) {
      const lastFail = existing.last_failed_at ? new Date(existing.last_failed_at).getTime() : 0;
      if (now - lastFail <= ATTEMPT_WINDOW_MS) {
        attempts = (existing.failed_attempts || 0) + 1;
      } else {
        attempts = 1;
      }
      if (attempts >= MAX_FAILED_ATTEMPTS) {
        isLocked = true;
        remainingMinutes = LOCKOUT_MINUTES;
        lockedUntil = new Date(now + LOCKOUT_MS).toISOString();
      }

      await supabase.from("auth_security_status").update({
        failed_attempts: attempts,
        is_locked: isLocked,
        locked_until: lockedUntil,
        company_id: companyId || existing.company_id,
        employee_id: employeeId || existing.employee_id,
        last_failed_at: new Date().toISOString(),
        last_failed_reason: reason || "Invalid credentials",
        updated_at: new Date().toISOString(),
      }).eq("id", existing.id);
    } else {
      await supabase.from("auth_security_status").insert({
        account_type: "employee",
        identifier: panClean,
        company_id: companyId || null,
        employee_id: employeeId || null,
        failed_attempts: 1,
        is_locked: false,
        first_failed_at: new Date().toISOString(),
        last_failed_at: new Date().toISOString(),
        last_failed_reason: reason || "Invalid credentials",
      });
    }

    // Telemetry log
    supabase.from("auth_login_attempts").insert({
      company_id: companyId || null,
      account_type: "employee",
      identifier: panClean,
      employee_id: employeeId || null,
      status: isLocked ? "LOCKED" : "FAILED",
      reason: reason || "Invalid credentials",
    }).then(() => {}).catch(() => {});

    if (isLocked) {
      setLocalSec(targetKey, { lockedUntil: now + LOCKOUT_MS, attempts });
    }

    logLegacyAudit(supabase, companyId, "employee", employeeId, isLocked ? "EMPLOYEE_LOCKED" : "EMPLOYEE_LOGIN_FAILED", {
      pan: panClean,
      reason,
      failed_attempts: attempts,
      locked: isLocked,
    });

    return { isLocked, remainingMinutes, attempts };
  } catch (tableErr) {
    // Proceed to Local/Audit log fallback
  }

  // Priority 3: Local device counter & audit log fallback
  const local = getLocalSec(targetKey) || { attempts: 0, firstFail: now };
  if (now - (local.lastFail || now) > ATTEMPT_WINDOW_MS) {
    local.attempts = 1;
    local.firstFail = now;
  } else {
    local.attempts = (local.attempts || 0) + 1;
  }
  local.lastFail = now;

  let isLocked = false;
  let remainingMinutes = 0;
  if (local.attempts >= MAX_FAILED_ATTEMPTS) {
    isLocked = true;
    local.lockedUntil = now + LOCKOUT_MS;
    remainingMinutes = LOCKOUT_MINUTES;
  }
  setLocalSec(targetKey, local);

  logLegacyAudit(supabase, companyId, "employee", employeeId, isLocked ? "EMPLOYEE_LOCKED" : "EMPLOYEE_LOGIN_FAILED", {
    pan: panClean,
    reason: reason || "Invalid credentials",
    failed_attempts: local.attempts,
    locked: isLocked,
    locked_until: isLocked ? new Date(local.lockedUntil).toISOString() : null,
  });

  return { isLocked, remainingMinutes, attempts: local.attempts };
}

/**
 * Record a successful employee login, resetting the lockout counter
 */
export async function recordSuccessfulEmployeeLogin(supabase, companyId, employeeId, pan) {
  clearLocalSec(`emp_${employeeId || (pan || '').trim().toUpperCase()}`);
  const { error } = await supabase.rpc('record_session_event', { p_action: 'EMPLOYEE_LOGIN_SUCCESS', p_details: {}, p_company_id: companyId || null });
  if (error) console.error('Sign-in telemetry could not be recorded:', error.message);
}

/**
 * Record a failed admin login attempt and enforce 3-strike lockout
 */
export async function recordFailedAdminLogin(supabase, companyId, username, reason) {
  const uClean = (username || "").trim().toLowerCase();
  const now = Date.now();
  const targetKey = `admin_${uClean}`;

  // Priority 1: Atomic RPC
  try {
    const { data: rpcRes, error: rpcErr } = await supabase.rpc("handle_login_failure", {
      p_account_type: "admin",
      p_identifier: uClean,
      p_company_id: companyId || null,
      p_reason: reason || "Invalid password",
    });

    if (!rpcErr && rpcRes) {
      if (rpcRes.isLocked) {
        setLocalSec(targetKey, {
          lockedUntil: now + (rpcRes.remainingMinutes || LOCKOUT_MINUTES) * 60000,
          attempts: rpcRes.attempts || MAX_FAILED_ATTEMPTS,
        });
      }
      logLegacyAudit(supabase, companyId, "admin", null, rpcRes.isLocked ? "ADMIN_LOCKED" : "ADMIN_LOGIN_FAILED", {
        username: uClean,
        reason: reason || "Invalid password",
        failed_attempts: rpcRes.attempts,
        locked: rpcRes.isLocked,
      });

      return {
        isLocked: !!rpcRes.isLocked,
        remainingMinutes: rpcRes.remainingMinutes || 0,
        attempts: rpcRes.attempts || 1,
      };
    }
  } catch (e) {}

  // Priority 2: Direct table upsert
  try {
    const { data: existing } = await supabase
      .from("auth_security_status")
      .select("*")
      .eq("account_type", "admin")
      .eq("identifier", uClean)
      .maybeSingle();

    let attempts = 1;
    let isLocked = false;
    let remainingMinutes = 0;
    let lockedUntil = null;

    if (existing) {
      const lastFail = existing.last_failed_at ? new Date(existing.last_failed_at).getTime() : 0;
      if (now - lastFail <= ATTEMPT_WINDOW_MS) {
        attempts = (existing.failed_attempts || 0) + 1;
      } else {
        attempts = 1;
      }
      if (attempts >= MAX_FAILED_ATTEMPTS) {
        isLocked = true;
        remainingMinutes = LOCKOUT_MINUTES;
        lockedUntil = new Date(now + LOCKOUT_MS).toISOString();
      }

      await supabase.from("auth_security_status").update({
        failed_attempts: attempts,
        is_locked: isLocked,
        locked_until: lockedUntil,
        company_id: companyId || existing.company_id,
        last_failed_at: new Date().toISOString(),
        last_failed_reason: reason || "Invalid password",
        updated_at: new Date().toISOString(),
      }).eq("id", existing.id);
    } else {
      await supabase.from("auth_security_status").insert({
        account_type: "admin",
        identifier: uClean,
        company_id: companyId || null,
        failed_attempts: 1,
        is_locked: false,
        first_failed_at: new Date().toISOString(),
        last_failed_at: new Date().toISOString(),
        last_failed_reason: reason || "Invalid password",
      });
    }

    supabase.from("auth_login_attempts").insert({
      company_id: companyId || null,
      account_type: "admin",
      identifier: uClean,
      status: isLocked ? "LOCKED" : "FAILED",
      reason: reason || "Invalid password",
    }).then(() => {}).catch(() => {});

    if (isLocked) {
      setLocalSec(targetKey, { lockedUntil: now + LOCKOUT_MS, attempts });
    }

    logLegacyAudit(supabase, companyId, "admin", null, isLocked ? "ADMIN_LOCKED" : "ADMIN_LOGIN_FAILED", {
      username: uClean,
      reason,
      failed_attempts: attempts,
      locked: isLocked,
    });

    return { isLocked, remainingMinutes, attempts };
  } catch (e) {}

  // Priority 3: Local memory & audit log fallback
  const local = getLocalSec(targetKey) || { attempts: 0, firstFail: now };
  if (now - (local.lastFail || now) > ATTEMPT_WINDOW_MS) {
    local.attempts = 1;
    local.firstFail = now;
  } else {
    local.attempts = (local.attempts || 0) + 1;
  }
  local.lastFail = now;

  let isLocked = false;
  let remainingMinutes = 0;
  if (local.attempts >= MAX_FAILED_ATTEMPTS) {
    isLocked = true;
    local.lockedUntil = now + LOCKOUT_MS;
    remainingMinutes = LOCKOUT_MINUTES;
  }
  setLocalSec(targetKey, local);

  logLegacyAudit(supabase, companyId, "admin", null, isLocked ? "ADMIN_LOCKED" : "ADMIN_LOGIN_FAILED", {
    username: uClean,
    reason: reason || "Invalid password",
    failed_attempts: local.attempts,
    locked: isLocked,
    locked_until: isLocked ? new Date(local.lockedUntil).toISOString() : null,
  });

  return { isLocked, remainingMinutes, attempts: local.attempts };
}

/**
 * Record a successful admin login, resetting the failure counter
 */
export async function recordSuccessfulAdminLogin(supabase, companyId, username) {
  clearLocalSec(`admin_${(username || '').trim().toLowerCase()}`);
  const { error } = await supabase.rpc('record_session_event', { p_action: 'ADMIN_LOGIN_SUCCESS', p_details: {}, p_company_id: companyId || null });
  if (error) console.error('Sign-in telemetry could not be recorded:', error.message);
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. UNLOCK ACCOUNT (1-CLICK MANUAL RESET)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Unlock an employee account instantly
 */
export async function unlockEmployeeAccount(supabase, companyId, employeeId, pan, unlockedBy, company) {
  return { ok: false, error: 'Employee sign-in protection is managed by Supabase Auth. Use Forgot password to recover access, or review the account and Auth logs in Supabase.' };
}

/**
 * Unlock an admin account instantly
 */
export async function unlockAdminAccount(supabase, companyId, username, unlockedBy) {
  return { ok: false, message: 'Administrator sign-in protection is managed by Supabase Auth. Review Auth logs and account status in Supabase; legacy lockout controls cannot unlock it.' };
}

// Helper: insert legacy audit log safely without throwing
function logLegacyAudit(supabase, companyId, actorType, employeeId, action, details) {
  try {
    supabase.from("audit_logs").insert({
      company_id: companyId || null,
      actor_type: actorType,
      employee_id: employeeId || null,
      action,
      details: redactLogDetails({
        ...details,
        timestamp: new Date().toISOString(),
      }),
    }).then(() => {}).catch(() => {});
  } catch (e) {}
}

/**
 * Permanently clear security and authentication logs
 */
export async function clearSecurityLogs(supabase, companyId = null) {
  try {
    // 1. Delete from auth_login_attempts
    let attemptDelete = supabase.from("auth_login_attempts").delete();
    if (companyId) {
      attemptDelete = attemptDelete.eq("company_id", companyId);
    } else {
      attemptDelete = attemptDelete.neq("id", "00000000-0000-0000-0000-000000000000");
    }
    const { error: attErr } = await attemptDelete;
    if (attErr) throw attErr;

    // 2. Also clear authentication actions from legacy audit_logs
    const secActions = [
      "ADMIN_LOGIN", "EMPLOYEE_PIN_RESET", "EMPLOYEE_CHANGE_PIN", "ADMIN_LOGOUT", "EMPLOYEE_LOGOUT",
      "ADMIN_LOGIN_FAILED",
      "ADMIN_LOGIN_SUCCESS",
      "ADMIN_LOCKED",
      "ADMIN_UNLOCK",
      "EMPLOYEE_LOGIN_FAILED",
      "EMPLOYEE_LOGIN_SUCCESS",
      "EMPLOYEE_LOCKED",
      "EMPLOYEE_UNLOCK",
      "ADMIN_LOGIN_AMBIGUOUS_USERNAME",
    ];
    let auditDelete = supabase.from("audit_logs").delete().in("action", secActions);
    if (companyId) {
      auditDelete = auditDelete.eq("company_id", companyId);
    } else {
      auditDelete = auditDelete.neq("id", "00000000-0000-0000-0000-000000000000");
    }
    const { error: auditError } = await auditDelete;
    if (auditError) throw new Error("Login-attempt records were cleared, but audit history could not be cleared: " + auditError.message);

    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.message || err };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. FETCH SECURITY OVERVIEW & THREAT DETECTIONS (HIGH SCALE)
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchSecurityOverview(supabase, companyId = null) {
  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const fiveMinsAgo = now - 5 * 60 * 1000;

  // Attempt 1: High-Performance Database Query on dedicated tables
  try {
    // 1. Fetch active lockouts in constant time O(1)
    let lockQuery = supabase
      .from("auth_security_status")
      .select("*, companies(name), employees(name, employee_code, pan)")
      .eq("is_locked", true)
      .gt("locked_until", new Date().toISOString());

    if (companyId) {
      lockQuery = lockQuery.eq("company_id", companyId);
    }

    const { data: dbLocks, error: lockErr } = await lockQuery;

    if (!lockErr && dbLocks !== null) {
      // Format active lockouts
      const activeLockouts = dbLocks.map((row) => {
        const lockUntilMs = new Date(row.locked_until).getTime();
        const remMins = Math.max(1, Math.ceil((lockUntilMs - now) / 60000));
        const isEmp = row.account_type === "employee";
        return {
          key: `${isEmp ? "EMP" : "ADM"}_${row.identifier}`,
          type: isEmp ? "Employee" : "Company Admin",
          target: row.identifier,
          employeeId: row.employee_id,
          companyId: row.company_id,
          name: row.employees?.name || (isEmp ? `PAN: ${row.identifier}` : row.identifier),
          code: row.employees?.employee_code || "—",
          pan: row.employees?.pan || (isEmp ? row.identifier : "—"),
          attempts: row.failed_attempts,
          remainingMinutes: remMins,
          lockedUntil: row.locked_until,
          lastFailedAt: row.last_failed_at,
          lastFailedReason: row.last_failed_reason,
        };
      });

      // 2. Fetch recent login attempts for brute-force spikes, full audit trail, and 24h metrics
      let attemptQuery = supabase
        .from("auth_login_attempts")
        .select("*, employees(name, employee_code)")
        .order("created_at", { ascending: false })
        .limit(300);

      if (companyId) {
        attemptQuery = attemptQuery.eq("company_id", companyId);
      }

      const { data: rawAttempts, error: attemptError } = await attemptQuery;
      if (attemptError) throw attemptError;
      const attempts = rawAttempts || [];

      // Calculate brute force alerts: 5+ failures in last 5 minutes, and 24h metrics
      const recentFailsByTarget = new Map();
      let failed24h = 0;
      let success24h = 0;

      attempts.forEach((att) => {
        const attTime = new Date(att.created_at).getTime();
        const is24h = attTime >= oneDayAgo;
        if (att.status === "FAILED" || att.status === "LOCKED") {
          if (is24h) failed24h++;
          if (attTime >= fiveMinsAgo) {
            const key = `${att.account_type}_${att.identifier}`;
            const cur = recentFailsByTarget.get(key) || {
              target: att.identifier,
              type: att.account_type === "employee" ? "Employee (PAN)" : "Admin (Username)",
              count: 0,
              lastAttempt: att.created_at,
              companyId: att.company_id,
            };
            cur.count++;
            recentFailsByTarget.set(key, cur);
          }
        } else if (att.status === "SUCCESS") {
          if (is24h) success24h++;
        }
      });

      const bruteForceAlerts = [];
      recentFailsByTarget.forEach((item) => {
        if (item.count >= 5) {
          bruteForceAlerts.push({
            target: item.target,
            type: item.type,
            failCount: item.count,
            lastAttempt: item.lastAttempt,
            companyId: item.companyId,
          });
        }
      });

      // Format logs list for display
      const logs = attempts.map((a) => {
        const isSuperAdmin = a.account_type === "admin" && String(a.identifier).toLowerCase() === "admin" && !a.company_id;
        const displayActor = isSuperAdmin ? "SUPER ADMIN" : a.account_type.toUpperCase();
        const actionPrefix = isSuperAdmin ? "SUPER_ADMIN" : a.account_type.toUpperCase();
        return {
          id: `auth:${a.id}`, source: "auth", source_id: a.id,
          company_id: a.company_id, employee_id: a.employee_id,
          created_at: a.created_at,
          actor_type: displayActor,
          action: a.status === "SUCCESS"
            ? `${actionPrefix}_LOGIN_SUCCESS`
            : a.status === "LOCKED"
            ? `${actionPrefix}_LOCKED`
            : a.status === "UNLOCKED"
            ? `${actionPrefix}_UNLOCK`
            : `${actionPrefix}_LOGIN_FAILED`,
          employees: a.employees,
          details: {
            pan: a.account_type === "employee" ? a.identifier : undefined,
            username: a.account_type === "admin" ? a.identifier : undefined,
            reason: a.reason,
            unlocked_by: a.metadata?.unlocked_by,
            failed_attempts: a.metadata?.attempts,
          },
        };
      });

      let eventQuery = supabase.from("audit_logs").select("*, employees(name, employee_code)")
        .in("action", ["EMPLOYEE_PIN_RESET", "EMPLOYEE_CHANGE_PIN", "ADMIN_LOGOUT", "EMPLOYEE_LOGOUT"])
        .order("created_at", { ascending: false }).limit(100);
      if (companyId) eventQuery = eventQuery.eq("company_id", companyId);
      const { data: credentialEvents, error: eventError } = await eventQuery;
      const history = [...logs, ...(!eventError ? credentialEvents || [] : []).map(row => ({ ...row, id: `audit:${row.id}`, source: "audit", source_id: row.id }))];
      return {
        activeLockouts, bruteForceAlerts, failed24h, success24h,
        totalSecurityLogs: history.length, logs: history, engineStatus: "DB_INDEXED",
        historyWarning: eventError ? "Credential-change and sign-out events could not be loaded." : "",
      };
    }
  } catch (dbErr) {
    // Dedicated tables don't exist yet; proceed to audit_logs fallback
  }

  // Attempt 2: Fallback query on audit_logs
  try {
    const securityActions = ["ADMIN_LOGIN", "ADMIN_LOGIN_FAILED", "ADMIN_LOGIN_SUCCESS", "ADMIN_LOCKED", "ADMIN_UNLOCK", "EMPLOYEE_LOGIN_FAILED", "EMPLOYEE_LOGIN_SUCCESS", "EMPLOYEE_LOCKED", "EMPLOYEE_UNLOCK", "ADMIN_LOGIN_AMBIGUOUS_USERNAME", "EMPLOYEE_PIN_RESET", "EMPLOYEE_CHANGE_PIN", "ADMIN_LOGOUT", "EMPLOYEE_LOGOUT"];
    let query = supabase
      .from("audit_logs")
      .select("*, employees(name, employee_code, pan)")
      .in("action", securityActions)
      .order("created_at", { ascending: false })
      .limit(300);

    if (companyId) {
      query = query.eq("company_id", companyId);
    }

    const { data: rawLogs, error } = await query;
    if (error) throw error;

    const logs = rawLogs || [];

    const secLogs = prepareLogRows(logs.filter(log => securityActions.includes(log.action))).map(log => ({ ...log, action: canonicalLogAction(log.action) }));

    const attemptsByTarget = new Map();
    secLogs.forEach((l) => {
      const isEmp = l.actor_type === "employee" || l.action.startsWith("EMPLOYEE_");
      const targetId = isEmp
        ? l.employee_id || l.details?.pan
        : (l.details?.username || "admin").toLowerCase();

      if (!targetId) return;
      const key = `${isEmp ? "EMP" : "ADM"}_${targetId}`;
      if (!attemptsByTarget.has(key)) {
        attemptsByTarget.set(key, []);
      }
      attemptsByTarget.get(key).push(l);
    });

    const activeLockouts = [];
    const bruteForceAlerts = [];

    attemptsByTarget.forEach((targetLogs, key) => {
      const isEmp = key.startsWith("EMP_");
      const target = key.replace(/^(EMP_|ADM_)/, "");
      const res = calculateLockFromLogs(targetLogs, isEmp ? "EMPLOYEE" : "ADMIN");

      if (res.isLocked) {
        const latestLog = targetLogs[0];
        activeLockouts.push({
          key,
          type: isEmp ? "Employee" : "Company Admin",
          target,
          employeeId: isEmp ? latestLog.employee_id : null,
          companyId: latestLog.company_id,
          name: latestLog.employees?.name || latestLog.details?.username || target,
          code: latestLog.employees?.employee_code || "—",
          pan: latestLog.employees?.pan || latestLog.details?.pan || (isEmp ? target : "—"),
          attempts: res.attempts,
          remainingMinutes: res.remainingMinutes,
          lockedUntil: res.lockedUntil,
          lastFailedAt: latestLog.created_at,
        });
      }

      const recentFails = targetLogs.filter(
        (l) =>
          (l.action === "EMPLOYEE_LOGIN_FAILED" || l.action === "ADMIN_LOGIN_FAILED") &&
          new Date(l.created_at).getTime() >= fiveMinsAgo
      );

      if (recentFails.length >= 5) {
        bruteForceAlerts.push({
          target,
          type: isEmp ? "Employee (PAN)" : "Admin (Username)",
          failCount: recentFails.length,
          lastAttempt: recentFails[0].created_at,
          companyId: recentFails[0].company_id,
        });
      }
    });

    const failed24h = secLogs.filter(
      (l) =>
        (l.action === "EMPLOYEE_LOGIN_FAILED" || l.action === "ADMIN_LOGIN_FAILED") &&
        new Date(l.created_at).getTime() >= oneDayAgo
    ).length;

    const success24h = secLogs.filter(
      (l) =>
        (l.action === "EMPLOYEE_LOGIN_SUCCESS" || l.action === "ADMIN_LOGIN_SUCCESS") &&
        new Date(l.created_at).getTime() >= oneDayAgo
    ).length;

    return {
      activeLockouts,
      bruteForceAlerts,
      failed24h,
      success24h,
      totalSecurityLogs: secLogs.length,
      logs: secLogs,
      engineStatus: "FALLBACK_AUDIT",
    };
  } catch (err) {
    return {
      activeLockouts: [],
      bruteForceAlerts: [],
      failed24h: 0,
      success24h: 0,
      totalSecurityLogs: 0,
      logs: [],
      historyWarning: err.message || "Authentication history could not be loaded.",
      engineStatus: "FALLBACK_AUDIT",
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. REACT COMPONENTS: BADGE & THREAT DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Inline status badge for employees table
 */
export function AccountLockBadge({ lockInfo, onUnlock }) {
  if (!lockInfo || !lockInfo.isLocked) return null;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        borderRadius: 6,
        background: "#fef2f2",
        border: "1px solid #fecaca",
        color: "#b91c1c",
        fontSize: 11,
        fontWeight: 700,
        marginLeft: 8,
        verticalAlign: "middle",
      }}
    >
      <span>🔒 Locked ({lockInfo.remainingMinutes}m)</span>
      {onUnlock && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUnlock();
          }}
          title="Click to unlock this account immediately"
          style={{
            background: "#b91c1c",
            color: "#ffffff",
            border: "none",
            borderRadius: 4,
            padding: "2px 6px",
            fontSize: 10,
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          Unlock
        </button>
      )}
    </span>
  );
}

/**
 * Full-page Security, Threat Detection & Attack Monitoring Dashboard
 */
export function SecurityThreatDashboard({
  supabase,
  activeCompany,
  companies,
  isMasterAdmin,
  currentUser,
  onBack,
  onOpenActivityLog,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState("");
  const securityRequestVersion = useRef(0);

  const companyMap = useMemo(() => {
    const map = new Map();
    (companies || []).forEach((c) => map.set(c.id, c.name));
    return map;
  }, [companies]);

  const loadData = useCallback(async () => {
    const version = ++securityRequestVersion.current;
    setLoading(true);
    const targetCompanyId = isMasterAdmin ? null : currentUser?.companyId || activeCompany?.id;
    if (!isMasterAdmin && !targetCompanyId) {
      setData({ logs: [], activeLockouts: [], bruteForceAlerts: [], failed24h: 0, success24h: 0, historyWarning: "Select a company to view its security history." });
      setLoading(false); return;
    }
    const result = await fetchSecurityOverview(supabase, targetCompanyId);
    if (version === securityRequestVersion.current) { setData(result); setLoading(false); }
  }, [supabase, isMasterAdmin, activeCompany?.id, currentUser?.companyId]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => { clearInterval(interval); securityRequestVersion.current++; };
  }, [loadData]);

  const handleUnlockEmployee = async (lock) => {
    const res = await unlockEmployeeAccount();
    alert(res.error);
  };

  const handleUnlockAdmin = async (lock) => {
    if (!window.confirm(`Unlock company admin account for "${lock.target}"?`)) return;
    const res = await unlockAdminAccount(
      supabase,
      lock.companyId,
      lock.target,
      currentUser?.username || "Super Admin"
    );
    if (res.ok) {
      setActionMsg(`✅ Company Admin "${lock.target}" has been unlocked.`);
      loadData();
      setTimeout(() => setActionMsg(""), 5000);
    } else {
      alert("Failed to unlock: " + res.error);
    }
  };

  const [clearingLogs, setClearingLogs] = useState(false);

  const handleClearLogs = async () => {
    const targetCompanyId = isMasterAdmin ? null : currentUser?.companyId || activeCompany?.id;
    if (!isMasterAdmin && !targetCompanyId) { setActionMsg("Select a company before clearing its logs."); return; }
    const scope = isMasterAdmin
      ? "ALL global security and authentication logs across all companies"
      : `all security logs for ${activeCompany?.name || "this company"}`;
    if (!window.confirm(`⚠️ Permanently clear ${scope}?\n\nThis will remove all recorded login attempts, lockout events, and authentication audit telemetry.\n\nThis action cannot be undone. Do you wish to proceed?`)) {
      return;
    }
    setClearingLogs(true);
    const res = await clearSecurityLogs(supabase, targetCompanyId);
    setClearingLogs(false);
    if (res.ok) {
      setActionMsg("✅ Security & authentication logs cleared successfully.");
      loadData();
      setTimeout(() => setActionMsg(""), 5000);
    } else {
      alert("Failed to clear security logs: " + (res.error || "Unknown error"));
    }
  };


  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header & Back Button */}
      {onBack && (
        <div
          onClick={onBack}
          style={{
            cursor: "pointer",
            color: "#2563eb",
            fontWeight: 600,
            fontSize: 13,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 16,
          }}
        >
          ← Back
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>🛡️ Security & Login Attack Monitor</span>
            {isMasterAdmin && (
              <span
                style={{
                  fontSize: 11,
                  background: "#1e293b",
                  color: "#f8fafc",
                  padding: "3px 10px",
                  borderRadius: 12,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Global SuperAdmin Mode
              </span>
            )}
          </div>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
            Automatic 3-strike login lockout defense (15 min lock), brute-force detection, and 1-click account unlocks.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onOpenActivityLog && (
            <button
              onClick={onOpenActivityLog}
              style={{
                background: "#ffffff",
                color: "#1e293b",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "8px 14px",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              }}
              title="Open the complete System Activity Log (approvals, payroll, profile changes, and audit trail)"
            >
              <span>📜</span> Activity Log
            </button>
          )}
          <button
            onClick={loadData}
            disabled={loading}
            style={{
              background: "#f1f5f9",
              color: "#334155",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {loading ? "⏳ Refreshing..." : "🔄 Refresh Status"}
          </button>
        </div>
      </div>

      {actionMsg && (
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            color: "#166534",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 20,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {actionMsg}
        </div>
      )}

      {/* Security Threat Alerts Banner */}
      {data?.bruteForceAlerts && data.bruteForceAlerts.length > 0 && (
        <div
          style={{
            background: "#fff1f2",
            border: "1.5px solid #fda4af",
            borderRadius: 12,
            padding: "16px 20px",
            marginBottom: 24,
            boxShadow: "0 4px 12px rgba(225, 29, 72, 0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 16,
              fontWeight: 800,
              color: "#be123c",
            }}
          >
            <span>🚨 High Alert: Potential Brute-Force Attack Detected!</span>
          </div>
          <div style={{ color: "#9f1239", fontSize: 13, marginTop: 6 }}>
            The system detected high-frequency failed attempts (5+ in under 5 minutes). Accounts have been protected by automatic lockout.
          </div>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {data.bruteForceAlerts.map((alert, idx) => (
              <div
                key={idx}
                style={{
                  background: "#ffffff",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid #fecdd3",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: 12.5,
                }}
              >
                <div>
                  <strong>Target:</strong> <span style={{ color: "#be123c" }}>{alert.target}</span> ({alert.type}) &bull;{" "}
                  <strong>Failed attempts:</strong> {alert.failCount} in past 5m
                  {alert.companyId && companyMap.has(alert.companyId) && (
                    <span style={{ color: "#64748b" }}> &bull; Company: {companyMap.get(alert.companyId)}</span>
                  )}
                </div>
                <div style={{ color: "#64748b" }}>
                  Last attempt: {new Date(alert.lastAttempt).toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 14,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "16px 20px",
            borderLeft: data?.activeLockouts?.length > 0 ? "5px solid #ef4444" : "5px solid #22c55e",
          }}
        >
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
            Locked Accounts
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: data?.activeLockouts?.length > 0 ? "#dc2626" : "#1e293b",
              marginTop: 4,
            }}
          >
            {data?.activeLockouts?.length || 0}
          </div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>
            {data?.activeLockouts?.length > 0 ? "⚠️ Requires admin attention" : "No active lockouts"}
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "16px 20px",
            borderLeft: "5px solid #f59e0b",
          }}
        >
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
            Failed Logins (24h)
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#d97706", marginTop: 4 }}>
            {data?.failed24h || 0}
          </div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>
            Blocked by authentication barrier
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "16px 20px",
            borderLeft: "5px solid #3b82f6",
          }}
        >
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
            Successful Logins (24h)
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#2563eb", marginTop: 4 }}>
            {data?.success24h || 0}
          </div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>
            Authenticated sessions
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "16px 20px",
            borderLeft: "5px solid #8b5cf6",
          }}
        >
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
            Lockout Rule
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#6b21a8", marginTop: 8 }}>
            3 Strikes &bull; 15 Mins
          </div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>
            Auto-unlock or 1-click admin reset
          </div>
        </div>
      </div>

      {/* ── SECTION 1: ACTIVE LOCKOUTS (WITH 1-CLICK UNLOCK) ── */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 28,
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e2e8f0",
            background: "#f8fafc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
              🔒 Currently Locked Accounts ({data?.activeLockouts?.length || 0})
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Accounts locked due to 3 consecutive failed attempts. Click "Unlock" to restore access immediately.
            </div>
          </div>
        </div>

        {(!data?.activeLockouts || data.activeLockouts.length === 0) ? (
          <div style={{ padding: "36px 20px", textAlign: "center", color: "#64748b" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>No Accounts Currently Locked</div>
            <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 4 }}>
              When a user exceeds 3 failed attempts, their lockout and 1-click unlock button will appear here.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#f1f5f9", borderBottom: "1.5px solid #cbd5e1" }}>
                <tr>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: "#475569" }}>Account Type</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: "#475569" }}>User / Identifier</th>
                  {isMasterAdmin && (
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: "#475569" }}>Company</th>
                  )}
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: 12, color: "#475569" }}>Failed Attempts</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: "#475569" }}>Lock Status</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: 12, color: "#475569" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.activeLockouts.map((lock) => (
                  <tr key={lock.key} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 700 }}>
                      <span
                        style={{
                          background: lock.type === "Employee" ? "#eff6ff" : "#fef3c7",
                          color: lock.type === "Employee" ? "#1d4ed8" : "#b45309",
                          padding: "2px 8px",
                          borderRadius: 6,
                          fontSize: 11,
                        }}
                      >
                        {lock.type}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13 }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{lock.name}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        {lock.type === "Employee" ? `PAN: ${lock.pan} | Emp ID: ${lock.code}` : `Username: ${lock.target}`}
                      </div>
                    </td>
                    {isMasterAdmin && (
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#334155" }}>
                        {companyMap.get(lock.companyId) || "Master Admin / System"}
                      </td>
                    )}
                    <td style={{ padding: "12px 16px", textAlign: "center", fontSize: 13, fontWeight: 700, color: "#dc2626" }}>
                      {lock.attempts} strikes
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 12 }}>
                      <span
                        style={{
                          background: "#fee2e2",
                          color: "#991b1b",
                          padding: "3px 8px",
                          borderRadius: 12,
                          fontWeight: 700,
                          display: "inline-block",
                        }}
                      >
                        🔒 Locked ({lock.remainingMinutes}m left)
                      </span>
                      <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 3 }}>
                        Until {new Date(lock.lockedUntil).toLocaleTimeString()}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      <button
                        onClick={() =>
                          lock.type === "Employee"
                            ? handleUnlockEmployee(lock)
                            : handleUnlockAdmin(lock)
                        }
                        style={{
                          background: "#2563eb",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: 6,
                          padding: "6px 14px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#1d4ed8")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#2563eb")}
                      >
                        {lock.type === "Employee" ? "Sign-in help" : "🔓 Unlock Account"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data?.historyWarning && <p role="alert" style={{color:"#92400e",fontSize:13}}>{data.historyWarning}</p>}
      <LogViewer title="Security & authentication log" logs={data?.logs || []} companies={companies || []} showCompany={isMasterAdmin} onClear={handleClearLogs} clearing={clearingLogs} scopeLabel={isMasterAdmin ? "all companies and the platform" : activeCompany?.name || "this company"} />
      <p style={{fontSize:12,color:"#64748b",marginTop:10}}>Recent history is limited to the loaded records. The 24-hour summaries are based on that history.</p>

    </div>
  );
}

export default SecurityThreatDashboard;
